/**
 * Partner Form Component
 * Handles form validation, submission, and user interactions
 */

/* global grecaptcha */

class PartnerForm {
    constructor(element) {
        this.element = element;
        this.form = element.querySelector('.partner-contact-form');
        this.submitButton = this.form?.querySelector('.submit-button');
        this.formInputs = this.form?.querySelectorAll('input, textarea');
        this.messagesContainer = element.querySelector('.form-messages');
        this.successMessage = element.querySelector('.success-message');
        this.errorMessage = element.querySelector('.error-message');
        
        // Get configuration from JSON script tag
        this.config = this.getConfig();
        
        this.init();
    }
    
    init() {
        if (!this.form) return;
        
        this.setupEventListeners();
        this.setupPhoneFormatting();
        this.setupAutoResize();
        this.setupKeyboardNavigation();
        this.setupFocusManagement();
    }
    
    getConfig() {
        const configScript = document.getElementById('partner-form-config');
        if (configScript) {
            try {
                return JSON.parse(configScript.textContent);
            } catch (e) {
                console.error('Failed to parse partner form config:', e);
            }
        }
        return {};
    }
    
    setupEventListeners() {
        // Form submission
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        
        // Real-time validation
        this.formInputs.forEach(input => {
            input.addEventListener('blur', () => this.validateField(input));
            input.addEventListener('input', () => this.clearFieldError(input));
        });
    }
    
    setupPhoneFormatting() {
        const phoneInput = this.form.querySelector('#phoneNumber');
        if (phoneInput) {
            phoneInput.addEventListener('input', (e) => this.formatPhoneNumber(e));
        }
    }
    
    setupAutoResize() {
        const textarea = this.form.querySelector('#message');
        if (textarea) {
            textarea.addEventListener('input', () => this.autoResizeTextarea(textarea));
        }
    }
    
    setupKeyboardNavigation() {
        this.form.addEventListener('keydown', (e) => this.handleKeyboardNavigation(e));
    }
    
    setupFocusManagement() {
        // Focus first input on load
        const firstInput = this.form.querySelector('input');
        if (firstInput) {
            firstInput.focus();
        }
    }
    
    formatPhoneNumber(e) {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length >= 6) {
            value = value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        } else if (value.length >= 3) {
            value = value.replace(/(\d{3})(\d{0,3})/, '$1-$2');
        }
        e.target.value = value;
    }
    
    autoResizeTextarea(textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.max(textarea.scrollHeight, 103) + 'px';
    }
    
    handleKeyboardNavigation(e) {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
            const inputs = Array.from(this.formInputs);
            const currentIndex = inputs.indexOf(e.target);
            const nextInput = inputs[currentIndex + 1];
            
            if (nextInput) {
                nextInput.focus();
            } else {
                // If it's the last field, submit the form
                this.form.dispatchEvent(new Event('submit'));
            }
        }
    }
    
    validateField(field) {
        const value = field.value.trim();
        const validation = this.getFieldValidation(field);
        
        if (!validation) return true;
        
        // Remove existing error styling and message
        this.clearFieldError(field);
        
        // Check if field is required and empty
        if (validation.required && !value) {
            this.showFieldError(field, 'This field is required');
            return false;
        }
        
        // Skip other validations if field is empty and not required
        if (!value && !validation.required) {
            return true;
        }
        
        // Check minimum length
        if (validation.minLength && value.length < validation.minLength) {
            this.showFieldError(field, validation.message);
            return false;
        }
        
        // Check pattern
        if (validation.pattern && !validation.pattern.test(value)) {
            this.showFieldError(field, validation.message);
            return false;
        }
        
        // Field is valid
        field.classList.add('success');
        return true;
    }
    
    getFieldValidation(field) {
        const validationData = field.getAttribute('data-validation');
        if (validationData) {
            try {
                const validation = JSON.parse(validationData);
                // Convert pattern string to RegExp if it exists
                if (validation.pattern && typeof validation.pattern === 'string') {
                    validation.pattern = new RegExp(validation.pattern);
                }
                return validation;
            } catch (e) {
                console.error('Failed to parse validation data:', e);
            }
        }
        return null;
    }
    
    showFieldError(field, message) {
        field.classList.add('error');
        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-message';
        errorDiv.textContent = message;
        field.parentNode.appendChild(errorDiv);
    }
    
    clearFieldError(field) {
        if (field.classList.contains('error')) {
            field.classList.remove('error');
            const errorMessage = field.parentNode.querySelector('.error-message');
            if (errorMessage) {
                errorMessage.remove();
            }
        }
    }
    
    async handleSubmit(e) {
        e.preventDefault();
        
        // Validate all fields
        let isFormValid = true;
        this.formInputs.forEach(input => {
            if (!this.validateField(input)) {
                isFormValid = false;
            }
        });
        
        if (!isFormValid) {
            // Scroll to first error
            const firstError = this.form.querySelector('.error');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                firstError.focus();
            }
            return;
        }
        
        // Show loading state
        this.setLoadingState(true);
        
        try {
            // Execute reCAPTCHA v3 and get token
            let recaptchaToken = '';
            if (this.config.recaptchaSiteKey && typeof grecaptcha !== 'undefined') {
                try {
                    recaptchaToken = await new Promise((resolve, reject) => {
                        grecaptcha.ready(() => {
                            grecaptcha.execute(this.config.recaptchaSiteKey, { action: 'partner_form_submit' })
                                .then(resolve)
                                .catch(reject);
                        });
                    });
                } catch (recaptchaError) {
                    console.error('reCAPTCHA error:', recaptchaError);
                    this.showErrorMessage('Security verification failed. Please refresh the page and try again.');
                    this.setLoadingState(false);
                    return;
                }
            }
            
            const formData = new FormData(this.form);
            // Add reCAPTCHA token to form data
            formData.set('g-recaptcha-response', recaptchaToken);
            
            const response = await fetch(this.config.formAction, {
                method: 'POST',
                body: formData,
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                const message = this.config.successMessage?.trim() 
                    || result.data?.message?.trim() 
                    || 'Thank you! Your message has been sent.';
                
                this.resetFormFields();
                this.showSuccessMessage(message);
            } else {
                const message = result.data?.message?.trim() 
                    || this.config.errorMessage?.trim() 
                    || 'There was an error submitting your form. Please try again.';
                this.showErrorMessage(message);
            }
            
        } catch (error) {
            console.error('Form submission error:', error);
            this.showErrorMessage('There was an error submitting your form. Please try again.');
        } finally {
            this.setLoadingState(false);
        }
    }
    
    setLoadingState(isLoading) {
        if (isLoading) {
            this.submitButton.classList.add('loading');
            this.submitButton.disabled = true;
        } else {
            this.submitButton.classList.remove('loading');
            this.submitButton.disabled = false;
        }
    }
    
    showSuccessMessage(message) {
        if (!this.successMessage || !this.messagesContainer) {
            console.error('PartnerForm: Message elements not found in DOM');
            return;
        }
        
        this.hideMessages();
        this.successMessage.querySelector('.message-text').textContent = message;
        this.successMessage.style.display = 'block';
        this.messagesContainer.style.display = 'block';
        this.messagesContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        setTimeout(() => this.hideMessages(), 5000);
    }
    
    showErrorMessage(message) {
        if (!this.errorMessage || !this.messagesContainer) {
            console.error('PartnerForm: Error message elements not found in DOM');
            return;
        }
        
        this.hideMessages();
        this.errorMessage.querySelector('.message-text').textContent = message;
        this.errorMessage.style.display = 'block';
        this.messagesContainer.style.display = 'block';
        this.messagesContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        
        setTimeout(() => this.hideMessages(), 5000);
    }
    
    hideMessages() {
        this.messagesContainer.style.display = 'none';
        this.successMessage.style.display = 'none';
        this.errorMessage.style.display = 'none';
    }
    
    resetFormFields() {
        this.form.reset();
        this.formInputs.forEach(input => {
            input.classList.remove('success', 'error');
        });
    }
    
    resetForm() {
        this.resetFormFields();
        this.hideMessages();
    }
}

// Initialize partner forms when DOM is ready
function initPartnerForms() {
    const partnerForms = document.querySelectorAll('[data-module="partner-form"]');
    partnerForms.forEach(form => new PartnerForm(form));
}

// Export for use in main bundle
export default function init() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPartnerForms);
    } else {
        initPartnerForms();
    }
}
